import React from 'react';
import { isEmpty, isNil } from 'lodash';

import ClaimLink from '~/components/ClaimLink';
import { LoadingSwitch, SortableTable, Text } from '~/components/core';
import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type { PaginationState } from '~/hooks/useIdBasedPagination';

import TEXTS from '../texts';
import type { SorIssue } from '../types';

import SorIssueStatusChip from './SorIssueStatusChip';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

interface SorIssuesTableProps {
  issues: SorIssue[];
  isLoading: boolean;
  isError: boolean;
  pagination: PaginationState;
  userName: (userId: string) => string;
  onRefresh: () => void;
  onIssueClick: (issue: SorIssue) => void;
}

const SorIssuesTable: React.FC<SorIssuesTableProps> = ({
  issues,
  isLoading,
  isError,
  pagination,
  userName,
  onRefresh,
  onIssueClick,
}) => {
  const columns = [
    {
      id: 'issue_status',
      label: TEXTS.issues.status,
      specialCell: (issue: SorIssue) => <SorIssueStatusChip status={issue.issue_status} />,
    },
    { id: 'label', label: TEXTS.issues.operation },
    {
      id: 'claim_number',
      label: TEXTS.issues.claim,
      // The link opens the claim, not the attempt's details.
      disableRowSelectionEvent: true,
      specialCell: (issue: SorIssue) => <ClaimLink claimId={issue.claim_id} linkText={issue.claim_number} />,
    },
    {
      id: 'requested_by_user_id',
      label: TEXTS.issues.requestedBy,
      specialCell: (issue: SorIssue) => userName(issue.requested_by_user_id),
    },
    {
      id: 'started_at',
      label: TEXTS.issues.started,
      specialCell: (issue: SorIssue) => serverDateTimeToLocal(issue.started_at),
    },
    {
      id: 'sor_latency_ms',
      label: TEXTS.issues.sorLatency,
      specialCell: (issue: SorIssue) =>
        isNil(issue.sor_latency_ms) ? TEXTS.issues.none : TEXTS.latency(issue.sor_latency_ms),
    },
    {
      id: 'tries',
      label: TEXTS.issues.tries,
      specialCell: (issue: SorIssue) => TEXTS.issues.triesValue(issue.tries, issue.max_tries),
    },
  ];

  return (
    <LoadingSwitch isLoading={isLoading && isEmpty(issues)} isError={isError}>
      <SortableTable
        rows={issues}
        columns={columns}
        onRowClick={(attemptId: number) => {
          const issue = issues.find(({ id }) => id === attemptId);
          if (issue) {
            onIssueClick(issue);
          }
        }}
        disableSortByUser
        keepRowsOrder
        stickyHeader
        paginationProps={{
          page: pagination.page,
          count: pagination.totalCount,
          rowsPerPage: pagination.rowsPerPage,
          onChangePage: (_event: unknown, page: number) => pagination.setPage(page),
          onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) =>
            pagination.setRowsPerPage(parseInt(event.target.value, 10)),
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
        }}
        counterViewText={
          <CounterWithRefresh
            onRefresh={onRefresh}
            isLoading={isLoading}
            pagination={pagination}
            refreshTooltipText={TEXTS.issues.refresh}
            refreshAriaLabel={TEXTS.issues.refresh}
          />
        }
        emptyStateComponent={
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="py-8 text-center">
            {TEXTS.issues.noIssues}
          </Text>
        }
      />
      {pagination.resultsTruncated && (
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="text-orange-600 mt-8">
          {TEXTS.issues.truncatedResults(pagination.totalCount)}
        </Text>
      )}
    </LoadingSwitch>
  );
};

export default SorIssuesTable;
